import { PermissionKey } from '@/modules/auth/constants/PermissionKey';
import { GuidanceStepFeatureFlagFilePrefix } from '@/modules/guidancestep/constants/GuidanceStepFeatureFlagFilePrefix';
import { getters } from '/@/modules/core/app/helpers/store';

export class AuthUserPermission {
  keys: string[];

  constructor(model: Partial<AuthUserPermission> = {}) {
    this.keys = model.keys ?? [];
  }

  can(key: string): boolean {
    return !!this.keys?.includes(key);
  }

  canAccessNewStepKey(stepKey: number): boolean {
    const key: string = `${GuidanceStepFeatureFlagFilePrefix.CAN_ACCESS_NEW_STEP}${stepKey}`;

    return this.can(key);
  }

  get canAccessAccounting(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ACCOUNTING);
  }

  get canAddAdvisorQuestions(): boolean {
    return this.can(PermissionKey.CAN_ADD_ADVISOR_QUESTIONS);
  }

  get canAccessReferQuebecWitnessedWillPlans(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_REFER_QUEBEC_WITNESSED_WILL_PLANS);
  }

  get canAccessAdminRoutes(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADMIN_ROUTES);
  }

  get canAccessAdvisorAdminAnalytics(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_ADMIN_ANALYTICS);
  }

  get canAccessAdvisorBookingLink(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_BOOKING_LINK);
  }

  get canAccessAdvisorClientJourney(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_CLIENT_JOURNEY);
  }

  get canAccessAdvisorDocumentsTab(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_DOCUMENTS_TAB);
  }

  get canAccessAdvisorFamilyTree(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_FAMILY_TREE);
  }

  get canAccessAdvisorPro(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ADVISOR_PRO);
  }

  get canAccessAftercareConsultation(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_AFTERCARE_CONSULTATION);
  }

  get canAccessAiResearch(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_AI_RESEARCH);
  }

  get canAccessAllConversations(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ALL_CONVERSATIONS);
  }

  get canAccessAllTimeEntries(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ALL_TIME_ENTRIES);
  }

  get canAccessAlternateLiquidationPath(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ALTERNATE_LIQUIDATION_PATH);
  }

  get canAccessAnnualReview(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ANNUAL_REVIEW);
  }

  get canAccessAnnualReviewV2(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ANNUAL_REVIEW_V2);
  }

  get canAccessBankStatementParser(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_BANK_STATEMENT_PARSER);
  }

  get canAccessBilling(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_BILLING);
  }

  get canAccessBillingSandbox(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_BILLING_SANDBOX);
  }

  get canAccessClaraContentLibrary(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLARA_CONTENT_LIBRARY);
  }

  get canAccessClaraForAdvisors(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLARA_FOR_ADVISORS);
  }

  get canAccessClaraForPlanners(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLARA_FOR_PLANNERS);
  }

  get canAccessClaraInboxDrafts(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLARA_INBOX_DRAFTS);
  }

  get canAccessClauseLibrary(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLAUSE_LIBRARY);
  }

  get canAccessClearEstateFileManagementPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_ESTATE_FILE_MANAGEMENT_PAGE);
  }

  get canAccessClearWillBilling(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_BILLING);
  }

  get canAccessClearWillDashboardPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_DASHBOARD_PAGE);
  }

  get canAccessClearWillEstateAccountingPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_ESTATE_ACCOUNTING_PAGE);
  }

  get canAccessClearWillFileManagementPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_FILE_MANAGEMENT_PAGE);
  }

  get canAccessConquestExtraction(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CONQUEST_EXTRACTION);
  }

  get canAccessContactsPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CONTACTS_PAGE);
  }

  get canAccessCouplesSpaceCleardesk(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_COUPLES_SPACE_CLEARDESK);
  }

  get canAccessDetectAuthSessionMismatch(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DETECT_AUTH_SESSION_MISMATCH);
  }

  get canViewDistributionNoLongerAvailable(): boolean {
    return this.can(PermissionKey.CAN_VIEW_DISTRIBUTION_NO_LONGER_AVAILABLE);
  }

  get canAccessFinalEstateReportInClearDesk(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FINAL_ESTATE_REPORT_IN_CLEAR_DESK);
  }

  get canAccessSelfServeFlow(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_SELF_SERVE_FLOW);
  }

  get canAccessStandardPlanGuidanceSteps(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_STANDARD_PLAN_GUIDANCE_STEPS);
  }

  get canAccessStandardPlanWillReviewMeeting(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_STANDARD_PLAN_WILL_REVIEW_MEETING);
  }

  get canAccessStep447431(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_STEP_447431);
  }

  get canAccessCashflowsV2(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CASHFLOWS_V2);
  }

  get canAccessClearWillInOverview(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_IN_OVERVIEW);
  }

  get canAccessClearWillInboxPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_INBOX_PAGE);
  }

  get canAccessClearWillPoas(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_CLEAR_WILL_POAS);
  }

  get canAccessDemoData(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DEMO_DATA);
  }

  get canAccessDefineYourSituationStage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DEFINE_YOUR_SITUATION_STAGE);
  }

  get canAccessDocuSign(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DOCU_SIGN);
  }

  get canAccessDocumentBuilder(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DOCUMENT_BUILDER);
  }

  get canAccessDocumentSharing(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DOCUMENT_SHARING);
  }

  get canAccessDocuments(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_DOCUMENTS);
  }

  get canAccessEmailChange(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_EMAIL_CHANGE);
  }

  get canAccessEmailOtp(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_EMAIL_OTP);
  }

  get canAccessEstateDemoTemplateCreation(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_DEMO_TEMPLATE_CREATION);
  }

  get canAccessEstateDiagnosticTool(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_DIAGNOSTIC_TOOL);
  }

  get canAccessEstateDistribution(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_DISTRIBUTION);
  }

  get canAccessEstateRag(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_RAG);
  }

  get canAccessEstateSettings(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_SETTINGS);
  }

  get canAccessFinalTaxesCalculatorTool(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FINAL_TAXES_CALCULATOR_TOOL);
  }

  get canAccessFirstNationsDisclaimers(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FIRST_NATIONS_DISCLAIMERS);
  }

  get canAccessLegacyLetters(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_LEGACY_LETTERS);
  }

  get canViewLegacyLetters(): boolean {
    return this.can(PermissionKey.CAN_VIEW_LEGACY_LETTERS);
  }

  get canAccessLidaapOnboarding(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_LIDAAP_ONBOARDING);
  }

  get canAccessLidaapStandalone(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_LIDAAP_STANDALONE);
  }

  get canAccessFamilyPatrimonyLearnMoreScreen(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FAMILY_PATRIMONY_LEARN_MORE_SCREEN);
  }

  get canAccessFamilyTree(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FAMILY_TREE);
  }

  get canAccessFlinks(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FLINKS);
  }

  get canAccessFlinksInvestment(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FLINKS_INVESTMENT);
  }

  get canAccessFlinksSandbox(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FLINKS_SANDBOX);
  }

  get canAccessFuneralArrangementStageV2(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_FUNERAL_ARRANGEMENT_STAGE_V2);
  }

  get canAccessGeneratedDocuments(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_GENERATED_DOCUMENTS);
  }

  get canAccessGuidanceSteps(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_GUIDANCE_STEPS);
  }

  get canAccessHelpCenter(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_HELP_CENTER);
  }

  get canAccessGuides(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_GUIDES);
  }

  get canAccessHeadlessCalendlyBooking(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_HEADLESS_CALENDLY_BOOKING);
  }

  get canAccessInbox(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_INBOX);
  }

  get canAccessInboxV2(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_INBOX_V2);
  }

  get canAccessInboxImpersonation(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_INBOX_IMPERSONATION);
  }

  get canAccessInboxInOverview(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_INBOX_IN_OVERVIEW);
  }

  get canAccessInboxVideo(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_INBOX_VIDEO);
  }

  get canAccessEstateClara(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_ESTATE_CLARA);
  }

  get canAccessMeetings(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_MEETINGS);
  }

  get canAccessMembersPage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_MEMBERS_PAGE);
  }

  get canAccessMobileDocumentScan(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_MOBILE_DOCUMENT_SCAN);
  }

  get canAccessNeedsNotifyCreditorsUpsell(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEEDS_NOTIFY_CREDITORS_UPSELL);
  }

  get canAccessNeedsWillSearchUpsell(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEEDS_WILL_SEARCH_UPSELL);
  }

  get canAccessNewAdvisorProUpsellModal(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEW_ADVISOR_PRO_UPSELL_MODAL);
  }

  get canAccessNewEditorWillReviewStepsFlow(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEW_EDITOR_WILL_REVIEW_STEPS_FLOW);
  }

  get canAccessNewPoas(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEW_POAS);
  }

  get canAccessNewPrimaryWill(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEW_PRIMARY_WILL);
  }

  get canAccessNewSecondaryWill(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEW_SECONDARY_WILL);
  }

  get canAccessSelfServeEditorWill(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_SELF_SERVE_EDITOR_WILL);
  }

  get canAccessSmsOtp(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_SMS_OTP);
  }

  get canAccessSolutionPicker(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_SOLUTION_PICKER);
  }

  get canAccessNextStepWidget(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NEXT_STEP_WIDGET);
  }

  get canAccessNlpParseDriversLicense(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NLP_PARSE_DRIVERS_LICENSE);
  }

  get canAccessNotificationCenter(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NOTIFICATION_CENTER);
  }

  get canAccessNova(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_NOVA);
  }

  get canAccessOtp(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_OTP);
  }

  get canAccessPartners(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PARTNERS);
  }

  get canAccessPermissionSets(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PERMISSION_SETS);
  }

  get canAccessPetStep(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PET_STEP);
  }

  get canAccessPlaid(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PLAID);
  }

  get canAccessPlaidSandbox(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PLAID_SANDBOX);
  }

  get canAccessPlanningOnboardingRedesign(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PLANNING_ONBOARDING_REDESIGN);
  }

  get canAccessPoaExtraction(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_POA_EXTRACTION);
  }

  get canAccessPoaInstructionImprovements(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_POA_INSTRUCTION_IMPROVEMENTS);
  }

  get canAccessPostOnboardingIntakeMeetingFlow(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_POST_ONBOARDING_INTAKE_MEETING_FLOW);
  }

  /**
   * @deprecated the protection mandate product key is deprecated and included in self-serve plans in QC
   */
  get canAccessProtectionMandate(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PROTECTION_MANDATE);
  }

  get canAccessProfessionalExecutorUpsell(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_PROFESSIONAL_EXECUTOR_UPSELL);
  }

  get canAccessTeamsSession(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TEAMS_SESSION);
  }

  get canAccessQuebecDistribution(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_QUEBEC_DISTRIBUTION);
  }

  get canAccessResources(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_RESOURCES);
  }

  get canAccessSpotlightSearch(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_SPOTLIGHT_SEARCH);
  }

  get canAccessStandardPlan(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_STANDARD_PLAN);
  }

  get canAccessTaxes(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TAXES);
  }

  get canAccessTeamWorkload(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TEAM_WORKLOAD);
  }

  get canAccessTeams(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TEAMS);
  }

  get canAccessTemplates(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TEMPLATES);
  }

  get canAccessTimeSavingsWidget(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TIME_SAVINGS_WIDGET);
  }

  get canAccessTranslations(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TRANSLATIONS);
  }

  get canAccessTriage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_TRIAGE);
  }

  get canAccessUsFinalizeInventoryValueStage(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_US_FINALIZE_INVENTORY_VALUE_STAGE);
  }

  get canAccessUserAssignment(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_USER_ASSIGNMENT);
  }

  get canAccessUserLinking(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_USER_LINKING);
  }

  get canAccessUsers(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_USERS);
  }

  get canAccessWelcomeFlow(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_WELCOME_FLOW);
  }

  get canAccessWillReviewAcknowledgment(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_WILL_REVIEW_ACKNOWLEDGMENT);
  }

  get canAccessWillReviewMeeting(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_WILL_REVIEW_MEETING);
  }

  get canAccessWorkflows(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_WORKFLOWS);
  }

  get canAccessInAppAnnouncements(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_IN_APP_ANNOUNCEMENTS);
  }

  get canArchiveInboxConversations(): boolean {
    return this.can(PermissionKey.CAN_ARCHIVE_INBOX_CONVERSATIONS);
  }

  get canAssignConsultant(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_CONSULTANT);
  }

  get canAssignConsultantAdmin(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_CONSULTANT_ADMIN);
  }

  get canAssignEstateBankingRequestCaseManager(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_ESTATE_BANKING_REQUEST_CASE_MANAGER);
  }

  get canAssignEstateProfessional(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_ESTATE_PROFESSIONAL);
  }

  get canAssignEstateTaskToTeam(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_ESTATE_TASK_TO_TEAM);
  }

  get canAssignJuniorEstateProfessional(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_JUNIOR_ESTATE_PROFESSIONAL);
  }

  get canAssignOnlySelfToEstate(): boolean {
    return !this.canAssignUserToEstate;
  }

  get canAssignProfessionalExecutor(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_PROFESSIONAL_EXECUTOR);
  }

  get canAssignUsers(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_USERS);
  }

  get canAssignTrustOfficer(): boolean {
    return this.can(PermissionKey.CAN_ASSIGN_TRUST_OFFICER);
  }

  get canAssignUserToEstate(): boolean {
    return (
      this.canAssignEstateProfessional ||
      this.canAssignJuniorEstateProfessional ||
      this.canAssignConsultant ||
      this.canAssignConsultantAdmin
    );
  }

  get canAutoApproveClauses(): boolean {
    return this.can(PermissionKey.CAN_AUTO_APPROVE_CLAUSES);
  }

  get canAutoPlayGuides(): boolean {
    return this.can(PermissionKey.CAN_AUTO_PLAY_GUIDES);
  }

  get canAutoRefreshFlinksBalance(): boolean {
    return this.can(PermissionKey.CAN_AUTO_REFRESH_FLINKS_BALANCE);
  }

  get canChangeEstate(): boolean {
    return this.can(PermissionKey.CAN_CHANGE_ESTATE);
  }

  get canCloseEstate(): boolean {
    return this.can(PermissionKey.CAN_CLOSE_ESTATE);
  }

  get canConnectUserToOutlook(): boolean {
    return this.can(PermissionKey.CAN_CONNECT_USER_TO_OUTLOOK);
  }

  get canCreateCompensationLiability(): boolean {
    return this.can(PermissionKey.CAN_CREATE_COMPENSATION_LIABILITY);
  }

  get canCreateConversations(): boolean {
    return this.can(PermissionKey.CAN_CREATE_CONVERSATIONS);
  }

  get canCreateCustomParalegal(): boolean {
    return this.can(PermissionKey.CAN_CREATE_CUSTOM_PARALEGAL);
  }

  get canCreateEstate(): boolean {
    return this.can(PermissionKey.CAN_CREATE_ESTATE);
  }

  get canCreateEstateFromHubSpot(): boolean {
    return this.can(PermissionKey.CAN_CREATE_ESTATE_FROM_HUB_SPOT);
  }

  get canCreateEstimatedFinalTaxes(): boolean {
    return this.can(PermissionKey.CAN_CREATE_ESTIMATED_FINAL_TAXES);
  }

  get canCreateTeams(): boolean {
    return this.can(PermissionKey.CAN_CREATE_TEAMS);
  }

  get canCreateVault(): boolean {
    return this.can(PermissionKey.CAN_CREATE_VAULT);
  }

  get canCreateWill(): boolean {
    return this.can(PermissionKey.CAN_CREATE_WILL);
  }

  get canDeleteDocument(): boolean {
    return this.can(PermissionKey.CAN_WRITE_DOCUMENT);
  }

  get canDeleteEstate(): boolean {
    return this.can(PermissionKey.CAN_DELETE_ESTATE);
  }

  get canDeleteUser(): boolean {
    return this.can(PermissionKey.CAN_WRITE_USER);
  }

  get canDisplayPrimaryEstateProfessionalPhoneNumberOnDashboardPage(): boolean {
    return this.can(
      PermissionKey.CAN_DISPLAY_PRIMARY_ESTATE_PROFESSIONAL_PHONE_NUMBER_ON_DASHBOARD_PAGE
    );
  }

  get canExportAccountingReport(): boolean {
    /**
     *  TODO: This should be done in the backend but since its a RBAC permission
     *  there is added complexity for handling the different estate plans. For now, we do it here.
     */
    return (
      this.can(PermissionKey.CAN_EXPORT_ACCOUNTING_REPORT) &&
      (!!getters.auth.getUser()?.currentEstate?.isEstateRepresentativePlan ||
        !!getters.auth.getUser()?.currentEstate?.isCompleteEstatePlan ||
        !!getters.auth.getUser()?.currentEstate?.isProbatePlan ||
        !!getters.auth.getUser()?.currentEstate?.isProbateAndTaxesPlan)
    );
  }

  get canExportNewAccountingReport(): boolean {
    return this.can(PermissionKey.CAN_EXPORT_NEW_ACCOUNTING_REPORT);
  }

  get canViewNewLawyerNotaryAssignment(): boolean {
    return this.can(PermissionKey.CAN_VIEW_NEW_LAWYER_NOTARY_ASSIGNMENT);
  }

  get canViewPlanningAssetLiabilityUpdates(): boolean {
    return this.can(PermissionKey.CAN_VIEW_PLANNING_ASSET_LIABILITY_UPDATES);
  }

  get canExportClearEstateSummary(): boolean {
    return this.can(PermissionKey.CAN_EXPORT_CLEAR_ESTATE_SUMMARY);
  }

  get canExportClearVaultSummary(): boolean {
    return this.can(PermissionKey.CAN_EXPORT_CLEAR_VAULT_SUMMARY);
  }

  get canForceShowCanadianEstateBankingAccount(): boolean {
    return this.can(PermissionKey.CAN_FORCE_SHOW_CANADIAN_ESTATE_BANKING_ACCOUNT);
  }

  get canGenerateEstateAiSummary(): boolean {
    return this.can(PermissionKey.CAN_GENERATE_ESTATE_AI_SUMMARY);
  }

  get canGenerateEstateSnapshot(): boolean {
    return this.can(PermissionKey.CAN_GENERATE_ESTATE_SNAPSHOT);
  }

  get canHideCompleteContactInfo(): boolean {
    return this.can(PermissionKey.CAN_HIDE_COMPLETE_CONTACT_INFO);
  }

  get canAccessMicroTip(): boolean {
    return this.can(PermissionKey.CAN_ACCESS_MICRO_TIP);
  }

  get canImpersonateUser(): boolean {
    return this.can(PermissionKey.CAN_IMPERSONATE_USER);
  }

  get canInviteBeneficiary(): boolean {
    return this.can(PermissionKey.CAN_INVITE_BENEFICIARY);
  }

  get canInviteContacts(): boolean {
    return this.can(PermissionKey.CAN_INVITE_CONTACTS);
  }

  get canInviteExecutor(): boolean {
    return (
      !getters.auth.getUser()?.currentEstate?.isClearVault ||
      this.can(PermissionKey.CAN_INVITE_EXECUTOR)
    );
  }

  get canInviteMembers(): boolean {
    return this.can(PermissionKey.CAN_INVITE_MEMBERS);
  }

  get canInviteProfessional(): boolean {
    return this.can(PermissionKey.CAN_INVITE_PROFESSIONAL);
  }

  get canLogIssue(): boolean {
    return this.can(PermissionKey.CAN_LOG_ISSUE);
  }

  get canLogTime(): boolean {
    return this.can(PermissionKey.CAN_LOG_TIME);
  }

  get canManageCompany(): boolean {
    return this.can(PermissionKey.CAN_MANAGE_COMPANY);
  }

  get canManageEstate(): boolean {
    return this.can(PermissionKey.CAN_MANAGE_ESTATE);
  }

  get canManageEstateBankingRequest(): boolean {
    return this.can(PermissionKey.CAN_MANAGE_ESTATE_BANKING_REQUEST);
  }

  get canManageEstateProfessionals(): boolean {
    return this.can(PermissionKey.CAN_MANAGE_ESTATE_PROFESSIONALS);
  }

  get canReadAsset(): boolean {
    return this.can(PermissionKey.CAN_READ_INVENTORY);
  }

  get canRequireOtp(): boolean {
    return this.can(PermissionKey.CAN_REQUIRE_OTP);
  }

  get canReadCashflows(): boolean {
    return this.can(PermissionKey.CAN_READ_CASHFLOWS);
  }

  get canReadConnection(): boolean {
    return this.can(PermissionKey.CAN_READ_CONNECTION);
  }

  get canReadContact(): boolean {
    return this.can(PermissionKey.CAN_READ_CONTACT);
  }

  get canReadDocument(): boolean {
    return this.can(PermissionKey.CAN_READ_DOCUMENT);
  }

  get canReadEstateDistribution(): boolean {
    return this.can(PermissionKey.CAN_READ_ESTATE_DISTRIBUTION);
  }

  get canReadInventory(): boolean {
    return this.can(PermissionKey.CAN_READ_INVENTORY);
  }

  get canReadLiability(): boolean {
    return this.can(PermissionKey.CAN_READ_INVENTORY);
  }

  get canReadMember(): boolean {
    return this.can(PermissionKey.CAN_READ_MEMBER);
  }

  get canReadPartnerUser(): boolean {
    return this.can(PermissionKey.CAN_READ_PARTNER_USER);
  }

  get canReadPermissionSets(): boolean {
    return this.can(PermissionKey.CAN_READ_PERMISSION_SETS);
  }

  get canReadProfessional(): boolean {
    return this.can(PermissionKey.CAN_READ_PROFESSIONAL);
  }

  get canReadTax(): boolean {
    return this.can(PermissionKey.CAN_READ_TAX);
  }

  get canViewEstateTaxForm(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ESTATE_TAX_FORM);
  }

  get canReadTeam(): boolean {
    return this.can(PermissionKey.CAN_READ_TEAM);
  }

  get canReadUser(): boolean {
    return this.can(PermissionKey.CAN_READ_USER);
  }

  get canReadWorkflows(): boolean {
    return this.can(PermissionKey.CAN_READ_WORKFLOWS);
  }

  get canRegenerateWill(): boolean {
    return this.can(PermissionKey.CAN_REGENERATE_WILL);
  }

  get canRegisterForMemoir(): boolean {
    return this.can(PermissionKey.CAN_REGISTER_FOR_MEMOIR);
  }

  get canSendDocumentMagicLinks(): boolean {
    return this.can(PermissionKey.CAN_SEND_DOCUMENT_MAGIC_LINKS);
  }

  get canSendStepReminderMessage(): boolean {
    return this.can(PermissionKey.CAN_SEND_STEP_REMINDER_MESSAGE);
  }

  get canShareDocumentsInInbox(): boolean {
    return this.can(PermissionKey.CAN_SHARE_DOCUMENTS_IN_INBOX);
  }

  get canShowUnsavedChangesWarningModal(): boolean {
    return this.can(PermissionKey.CAN_SHOW_UNSAVED_CHANGES_WARNING_MODAL);
  }

  get canToggleDeveloperMode(): boolean {
    return this.can(PermissionKey.CAN_TOGGLE_DEVELOPER_MODE);
  }

  get canToggleLanguage(): boolean {
    return this.can(PermissionKey.CAN_TOGGLE_LANGUAGE);
  }

  get canTransitionClearVaultToClearWillOnOverview(): boolean {
    return this.can(PermissionKey.CAN_TRANSITION_CLEAR_VAULT_TO_CLEAR_WILL_ON_OVERVIEW);
  }

  get canUseContactAliasesInGeneratedDocuments(): boolean {
    return this.can(PermissionKey.CAN_USE_CONTACT_ALIASES_IN_GENERATED_DOCUMENTS);
  }

  get canUseContactSelectorInSteps(): boolean {
    return this.can(PermissionKey.CAN_USE_CONTACT_SELECTOR_IN_STEPS);
  }

  get canUseDocIngestion(): boolean {
    return this.can(PermissionKey.CAN_USE_DOC_INGESTION);
  }

  get canUseDocumentAi(): boolean {
    return this.can(PermissionKey.CAN_USE_DOCUMENT_AI);
  }

  get canUseEditorAutosave(): boolean {
    return this.can(PermissionKey.CAN_USE_EDITOR_AUTOSAVE);
  }

  get canUseManitobaPoaPropertyHomestead(): boolean {
    return this.can(PermissionKey.CAN_USE_MANITOBA_POA_PROPERTY_HOMESTEAD);
  }

  get canUseSubmoduleStepUi(): boolean {
    return this.can(PermissionKey.CAN_USE_SUBMODULE_STEP_UI);
  }

  get canPayStandardPlanRecurringFee(): boolean {
    return this.can(PermissionKey.CAN_PAY_STANDARD_PLAN_RECURRING_FEE);
  }

  get canViewAdvisorProUpsells(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ADVISOR_PRO_UPSELLS);
  }

  get canViewAllEstateBankingRequestDetails(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ALL_ESTATE_BANKING_REQUEST_DETAILS);
  }

  get canViewAutomatedQaSelfServeBanner(): boolean {
    return this.can(PermissionKey.CAN_VIEW_AUTOMATED_QA_SELF_SERVE_BANNER);
  }

  get canViewBeneficiaryWidget(): boolean {
    return this.can(PermissionKey.CAN_VIEW_BENEFICIARY_WIDGET);
  }

  get canViewBillingPlan(): boolean {
    return this.can(PermissionKey.CAN_VIEW_BILLING_PLAN);
  }

  get canViewClearDesk(): boolean {
    return this.can(PermissionKey.CAN_VIEW_CLEAR_DESK);
  }

  get canViewClaraMaintenancePage(): boolean {
    return this.can(PermissionKey.CAN_VIEW_CLARA_MAINTENANCE_PAGE);
  }

  get canViewPoaPersonalCareMaintenanceWidget(): boolean {
    return this.can(PermissionKey.CAN_VIEW_POA_PERSONAL_CARE_MAINTENANCE_WIDGET);
  }

  get canViewPoaPropertyMaintenanceWidget(): boolean {
    return this.can(PermissionKey.CAN_VIEW_POA_PROPERTY_MAINTENANCE_WIDGET);
  }

  get canViewClearVaultIntakeQuestionStep(): boolean {
    return this.can(PermissionKey.CAN_VIEW_CLEAR_VAULT_INTAKE_QUESTION_STEP);
  }

  get canViewClearWillFinalVideo(): boolean {
    return this.can(PermissionKey.CAN_VIEW_CLEAR_WILL_FINAL_VIDEO);
  }

  get canViewCollaboratorsFolder(): boolean {
    return this.can(PermissionKey.CAN_VIEW_COLLABORATORS_FOLDER);
  }

  get canViewCompanyAnnouncement(): boolean {
    return this.can(PermissionKey.CAN_VIEW_COMPANY_ANNOUNCEMENT);
  }

  get canViewContactsTourGuide(): boolean {
    return this.can(PermissionKey.CAN_VIEW_CONTACTS_TOUR_GUIDE);
  }

  get canViewDisableVpnBanner(): boolean {
    return this.can(PermissionKey.CAN_VIEW_DISABLE_VPN_BANNER);
  }

  get canViewDocumentBuilderTourGuide(): boolean {
    return this.can(PermissionKey.CAN_VIEW_DOCUMENT_BUILDER_TOUR_GUIDE);
  }

  get canViewDocumentsTourGuide(): boolean {
    return this.can(PermissionKey.CAN_VIEW_DOCUMENTS_TOUR_GUIDE);
  }

  get canViewEsignRecipientsAndEnvelopeStatus(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ESIGN_RECIPIENTS_AND_ENVELOPE_STATUS);
  }

  get canViewEstatePlanMemo(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ESTATE_PLAN_MEMO);
  }

  get canViewExecutorAddendum(): boolean {
    return this.can(PermissionKey.CAN_VIEW_EXECUTOR_ADDENDUM);
  }

  get canViewExecutorCentre(): boolean {
    return this.can(PermissionKey.CAN_VIEW_EXECUTOR_CENTRE);
  }

  get canViewFinancialsTourGuide(): boolean {
    return this.can(PermissionKey.CAN_VIEW_FINANCIALS_TOUR_GUIDE);
  }

  get canViewIgTaxTab(): boolean {
    return this.can(PermissionKey.CAN_VIEW_IG_TAX_TAB);
  }

  get canViewInboxMembersList(): boolean {
    return this.can(PermissionKey.CAN_VIEW_INBOX_MEMBERS_LIST);
  }

  get canViewInboxRenameChat(): boolean {
    return this.can(PermissionKey.CAN_VIEW_INBOX_RENAME_CHAT);
  }

  get canViewInboxVideo(): boolean {
    return this.can(PermissionKey.CAN_VIEW_INBOX_VIDEO);
  }

  get canViewInboxViewFiles(): boolean {
    return this.can(PermissionKey.CAN_VIEW_INBOX_VIEW_FILES);
  }

  get canViewMarketplace(): boolean {
    return this.can(PermissionKey.CAN_VIEW_MARKETPLACE);
  }

  get canViewMeetingsTourGuide(): boolean {
    return this.can(PermissionKey.CAN_VIEW_MEETINGS_TOUR_GUIDE);
  }

  get canViewMembersWidget(): boolean {
    return this.can(PermissionKey.CAN_VIEW_MEMBERS_WIDGET);
  }

  get canViewNewFinancialAdvisorDashboard(): boolean {
    return this.can(PermissionKey.CAN_VIEW_NEW_FINANCIAL_ADVISOR_DASHBOARD);
  }

  /**
   * @deprecated Use canViewAutomatedQaSelfServeBanner instead.
   */
  get canViewQaSelfServeBanner(): boolean {
    return this.can(PermissionKey.CAN_VIEW_QA_SELF_SERVE_BANNER);
  }

  get canViewRealEstateTitleSearchUpsell(): boolean {
    return this.can(PermissionKey.CAN_VIEW_REAL_ESTATE_TITLE_SEARCH_UPSELL);
  }

  get canViewRoleConflictWarning(): boolean {
    return this.can(PermissionKey.CAN_VIEW_ROLE_CONFLICT_WARNING);
  }

  get canViewSpecificEstate(): boolean {
    return this.can(PermissionKey.CAN_VIEW_SPECIFIC_ESTATE);
  }

  get canViewUnassignedEstates(): boolean {
    return this.can(PermissionKey.CAN_VIEW_UNASSIGNED_ESTATES);
  }

  get canViewUserLoginInfo(): boolean {
    return this.can(PermissionKey.CAN_VIEW_USER_LOGIN_INFO);
  }

  get canWriteAccounting(): boolean {
    return this.canWriteAsset && this.canWriteLiability;
  }

  get canWriteAsset(): boolean {
    return this.can(PermissionKey.CAN_WRITE_INVENTORY);
  }

  get canWriteBeneficiaryInDistribution(): boolean {
    return this.can(PermissionKey.CAN_WRITE_BENEFICIARY_IN_DISTRIBUTION);
  }

  get canWriteCashflows(): boolean {
    return this.can(PermissionKey.CAN_WRITE_CASHFLOWS);
  }

  get canWriteCompany(): boolean {
    return this.can(PermissionKey.CAN_WRITE_COMPANY);
  }

  get canWriteConnection(): boolean {
    return this.can(PermissionKey.CAN_WRITE_CONNECTION);
  }

  get canWriteContact(): boolean {
    return this.can(PermissionKey.CAN_WRITE_CONTACT);
  }

  get canWriteDocument(): boolean {
    return this.can(PermissionKey.CAN_WRITE_DOCUMENT);
  }

  get canWriteEstateDistribution(): boolean {
    return this.can(PermissionKey.CAN_WRITE_ESTATE_DISTRIBUTION);
  }

  get canWriteEstateTaxForm(): boolean {
    return this.can(PermissionKey.CAN_WRITE_ESTATE_TAX_FORM);
  }

  get canWriteInventory(): boolean {
    return this.can(PermissionKey.CAN_WRITE_INVENTORY);
  }

  get canWriteLiability(): boolean {
    return this.can(PermissionKey.CAN_WRITE_INVENTORY);
  }

  get canWriteMember(): boolean {
    return this.can(PermissionKey.CAN_WRITE_MEMBER);
  }

  get canWriteNote(): boolean {
    return this.can(PermissionKey.CAN_WRITE_NOTE);
  }

  get canWritePartnerUser(): boolean {
    return this.can(PermissionKey.CAN_WRITE_PARTNER_USER);
  }

  get canWritePermissionSets(): boolean {
    return this.can(PermissionKey.CAN_WRITE_PERMISSION_SETS);
  }

  get canWriteProfessional(): boolean {
    return this.can(PermissionKey.CAN_WRITE_PROFESSIONAL);
  }

  get canWriteTax(): boolean {
    return this.can(PermissionKey.CAN_WRITE_TAX);
  }

  get canWriteTeam(): boolean {
    return this.can(PermissionKey.CAN_WRITE_TEAM);
  }

  get canWriteUser(): boolean {
    return this.can(PermissionKey.CAN_WRITE_USER);
  }

  get canWriteWorkflows(): boolean {
    return this.can(PermissionKey.CAN_WRITE_WORKFLOWS);
  }
}
